import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { listCustomers } from '../../api/customers';
import { Select } from '../ui/Input';

/**
 * CustomerPicker — dropdown to pick a customer.
 * 
 * Props:
 *   value             — selected customer id
 *   onChange          — (id: number|null) => void   — called with just the id (backward compat)
 *   onCustomerSelect  — (customer: object|null) => void — called with the full customer object
 */
export default function CustomerPicker({ value, onChange, onCustomerSelect, placeholder = 'Select customer...', ...rest }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: listCustomers,
    staleTime: 60_000,
  });

  const customers = Array.isArray(data) ? data : [];

  const handleChange = (e) => {
    const id = e.target.value ? Number(e.target.value) : null;
    onChange?.(id);
    if (onCustomerSelect) {
      const found = id ? customers.find((c) => c.id === id) || null : null;
      onCustomerSelect(found);
    }
  };

  return (
    <Select
      value={value ?? ''}
      onChange={handleChange}
      disabled={isLoading}
      {...rest}
    >
      <option value="">{isLoading ? 'Loading...' : placeholder}</option>
      {customers.map((c) => (
        <option key={c.id} value={c.id}>
          {c.customerCode ? `${c.customerCode} — ` : ''}
          {c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || `#${c.id}`}
        </option>
      ))}
    </Select>
  );
}
